"use client";

import { useState, type FormEvent } from "react";
import { useStoredUser } from "@/hooks/useStoredUser";

type Props = {
  heading?: string;
  body?: string;
  emailPlaceholder?: string;
  buttonText?: string;
  successMessage?: string;
  subscribedTemplate?: string;
};

export function NewsletterSubscribe({
  heading = "Stay in the Know",
  body = "Be the first to see new drops and private event invitations.",
  emailPlaceholder = "your@email.com",
  buttonText = "Subscribe",
  successMessage = "You're on the list.",
  subscribedTemplate = "You're subscribed as {{email}}."
}: Props) {
  const storedUser = useStoredUser();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  if (storedUser?.email) {
    const [before, after] = subscribedTemplate.split("{{email}}");
    return (
      <div className="space-y-3">
        <p className="text-xs uppercase tracking-[0.4em] text-slate-500">{heading}</p>
        <p className="text-sm text-slate-600">
          {before}
          <span className="font-medium text-slate-800">{storedUser.email}</span>
          {after}
        </p>
      </div>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    const trimmed = email.trim();
    if (!trimmed) return;

    setStatus("loading");
    setMessage("");

    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed })
      });

      const data = await res.json();

      if (!res.ok) {
        setStatus("error");
        setMessage(data.error || "Something went wrong.");
        return;
      }

      setStatus("success");
      setMessage(successMessage);
      setEmail("");
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Please try again.");
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-xs uppercase tracking-[0.4em] text-slate-500">{heading}</p>
      <p className="text-sm text-slate-600">{body}</p>

      {status === "success" ? (
        <p className="text-sm text-slate-700 font-medium">{message}</p>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={emailPlaceholder}
            aria-label="Email address"
            className="w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-slate-500 focus:outline-none sm:max-w-[220px]"
          />
          <button
            type="submit"
            disabled={status === "loading"}
            className="inline-flex items-center justify-center rounded-sm border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-slate-700 transition hover:border-slate-500 hover:text-slate-900 disabled:opacity-50"
          >
            {status === "loading" ? "..." : buttonText}
          </button>
        </form>
      )}

      {status === "error" && (
        <p className="text-xs text-red-600">{message}</p>
      )}
    </div>
  );
}
